/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';
import { TelemetryContext } from '@/contexts/telemetry-context';
import TextDiv from '@/components/text-div';
import getFileContents from '@/utilities/get-file-contents';
import PermissionGate from '@/components/permission-gate';
import validation from './validation';
import getAcceptedFilesList from './validation/get-accepted-files';

export default function FileUploader({
  inputSelectorId,
  onChange,
  onError,
  buttonText,
  currentFile,
  isClearable,
  clearButtonText,
  fileType,
  validationRules,
  enabled,
  showAsLink,
  showFilename,
  telemetryEvent,
  permissionId,
}) {
  const { translate } = useContext(LocaleContext);
  const telemetryContext = useContext(TelemetryContext);
  const [error, setError] = useState();
  const [fileName, setFileName] = useState();

  const handleError = (message) => {
    onError(translate(message));
    setError(message);
  };

  const fileChosen = async (contents) => {
    const file = contents?.target?.files?.[0];
    if (file) {
      const isValid = await validation({ file, fileType, validationRules });
      if (isValid.valid) {
        const fileContents = await getFileContents(file, fileType);
        onChange(fileContents);
        setFileName(file.name);
        handleError(null);
        if (telemetryEvent) {
          telemetryContext?.sendEvent(telemetryEvent, { type: 'image_added' });
        }
      } else if (isValid.error) {
        setFileName(null);
        handleError(isValid.error);
      }
    } else {
      setFileName(null);
    }
  };

  const handleButtonClick = () => {
    const fileInput = document.getElementById(inputSelectorId);
    if (isClearable && currentFile) {
      fileInput.value = '';
      onChange(null);
      setFileName(null);
    } else {
      fileInput.click();
    }
  };

  const getButtonText = () => {
    if (!isClearable) {
      return buttonText;
    }
    return currentFile ? clearButtonText : buttonText;
  };

  return (
    <PermissionGate permission={permissionId}>
      <div id={`${inputSelectorId}-wrapper`} key={inputSelectorId} className={`wrapper ${showAsLink ? 'link' : ''}`}>
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={!enabled}
        >
          {getButtonText()}
        </button>
        <input
          id={inputSelectorId}
          onChange={fileChosen}
          type="file"
          name="file"
          accept={getAcceptedFilesList(fileType)}
          hidden
        />
        {showFilename && fileName && (
          <div className={`file-name ${fileName ? ' visible' : ''}`}>
            <TextDiv>{fileName}</TextDiv>
          </div>
        )}
        <div className={`error ${error ? ' visible' : ''}`}>
          {error && <TextDiv>{translate(error)}</TextDiv>}
        </div>
        <style jsx>
          {`
          .wrapper {
            width: 100%;
            text-align: right;
          }

          button {
            min-width: 180px;
            height: 42px;
            border-radius: 21px;
            border: solid 1px var(--color-dark-grey);
            background-color: var(--color-transparent);
            cursor: pointer;
            align-self: center;
            outline: none;
            padding: 10px 20px;
            font-family: inherit;
            font-size: var(--font-size-H2);
          }

          .wrapper.link button{
            background: transparent;
            border-color: transparent;
            height: auto;
            min-width: auto;
            text-decoration: underline;
            padding: 0;
            font-family: inherit;
            font-size: inherit;
          }

          button:hover, 
          button:focus, 
          .wrapper.link button:focus {
            border: solid 1px var(--color-primary-dark);
          }

          button:disabled {
            color: var(--color-disabled-grey);
            border: solid 1px var(--color-disabled-grey);
          }

          .file-name {
            margin-top: 6px;
            font-size: var(--font-size-error);
          }

          .error {
            min-height: 20px;
            visibility: hidden;
            margin-top: 6px;
            white-space: break-spaces;
          }

          .wrapper.link .error{
            display: none;
          }

          .error.visible {
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            text-align: right;
            visibility: visible;
          }

          .error.rtl {
            text-align: left;
          }
        `}
        </style>
      </div>
    </PermissionGate>
  );
}

FileUploader.defaultProps = {
  buttonText: 'Choose file',
  validationRules: null,
  enabled: true,
  currentFile: null,
  isClearable: false,
  clearButtonText: null,
  onError: () => {},
  showAsLink: false,
  showFilename: true,
  telemetryEvent: null,
};

FileUploader.propTypes = {
  inputSelectorId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  buttonText: PropTypes.string,
  fileType: PropTypes.string.isRequired,
  currentFile: PropTypes.string,
  isClearable: PropTypes.bool,
  clearButtonText: PropTypes.string,
  validationRules: PropTypes.shape({}),
  enabled: PropTypes.bool,
  showAsLink: PropTypes.bool,
  showFilename: PropTypes.bool,
  telemetryEvent: PropTypes.string,
};
